import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { company, img } from "../mock";
import Seo from "../components/Seo";

const Section = ({ title, children }) => (
  <div className="acc-doc-block">
    <h2>{title}</h2>
    {children}
  </div>
);

const AccessibilityStatement = () => {
  useEffect(() => {
    const obs = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && e.target.classList.add("in")), { threshold: 0.08 });
    document.querySelectorAll(".mh-reveal").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div>
      <Seo
        title="Accessibility Statement"
        description="Monarch Homes of Brevard is committed to digital accessibility and conforming toward WCAG 2.1 Level AA standards for all visitors."
        path="/accessibility-statement"
      />
      <section className="mh-page-hero" style={{ backgroundImage: `url(${img.ext1})` }}>
        <div className="mh-page-hero-inner">
          <div className="mh-crumb"><Link to="/">Home</Link> / Accessibility Statement</div>
          <h1>Accessibility Statement</h1>
        </div>
      </section>

      <section className="mh-section">
        <div className="mh-container acc-doc mh-reveal">
          <p className="acc-doc-updated">Last Updated: February 27, 2026</p>

          <Section title="Our Commitment to Accessibility">
            <p>We are committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying relevant accessibility standards to enhance usability and inclusivity.</p>
            <p>Our goal is to provide a website that is accessible to the widest possible audience, regardless of technology or ability.</p>
          </Section>

          <Section title="Accessibility Standards">
            <p>We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA, which are widely recognized as the industry standard for website accessibility.</p>
            <p>Accessibility is an ongoing process, and we are actively working toward improving the accessibility and usability of our website.</p>
          </Section>

          <Section title="Accessibility Technology & Tools">
            <p>To support our accessibility efforts, we have deployed industry-standard accessibility technology designed to enhance usability and assist visitors with disabilities. These tools may provide features such as:</p>
            <ul>
              <li>Screen reader enhancements</li>
              <li>Keyboard navigation improvements</li>
              <li>Color contrast adjustments</li>
              <li>Text size and spacing controls</li>
              <li>Focus indicators</li>
              <li>Additional accessibility interface options</li>
            </ul>
            <p>This technology helps us improve accessibility while we continue broader auditing, remediation, and development efforts.</p>
          </Section>

          <Section title="Ongoing Improvement">
            <p>Accessibility is an ongoing initiative. We are:</p>
            <ul>
              <li>Reviewing site content and functionality regularly</li>
              <li>Working with developers and accessibility tools</li>
              <li>Identifying and remediating barriers</li>
              <li>Incorporating accessibility into future design and development</li>
            </ul>
            <p>We understand accessibility is a continuous journey and are committed to making improvements over time.</p>
          </Section>

          <Section title="Third-Party Content">
            <p>Some areas of the website may include third-party content, plugins, or integrations that are not fully controlled by us. While we encourage vendors to provide accessible content, we cannot guarantee accessibility of third-party features. However, we welcome feedback regarding any issues encountered.</p>
          </Section>

          <Section title="Feedback & Assistance">
            <p>If you experience difficulty accessing any part of this website, need assistance, or would like to request content in an alternative format, please contact us. Please include:</p>
            <ul>
              <li>The page URL</li>
              <li>Description of the issue</li>
              <li>Your preferred contact method</li>
            </ul>
            <p>You can reach us via our <Link to="/contact">Contact Us page</Link>, or call <a href={company.phoneHref}>{company.phone}</a>. We aim to respond within a reasonable timeframe and will make reasonable efforts to provide accessible alternatives.</p>
          </Section>

          <Section title="Good Faith Efforts">
            <p>We are making good-faith efforts to comply with accessibility standards and improve usability for individuals with disabilities. Despite our efforts, some content may not yet fully meet the strictest accessibility standards due to the evolving nature of technology and ongoing updates. We appreciate your understanding as we continue improving accessibility.</p>
          </Section>

          <Section title="Terms & Conditions - Accessibility Clause">
            <p>We are committed to providing an accessible website experience for all users and strive to follow recognized accessibility standards, including WCAG guidelines. We have implemented industry-standard accessibility tools and continue to review, update, and improve our website to enhance accessibility.</p>
            <p>Because accessibility is an ongoing process, some areas of the website may not yet fully conform to all accessibility standards. If you encounter accessibility barriers, please contact us so we can provide assistance, offer alternative access, and address the issue where feasible. Nothing in these Terms is intended to limit any rights available under applicable accessibility laws.</p>
          </Section>
        </div>
      </section>
    </div>
  );
};

export default AccessibilityStatement;
